// src/pages/Admin/Appointment/AllPetVaccines.jsx
import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import { FaSyringe, FaTimes, FaPaw, FaUserMd, FaBell, FaCheckCircle, FaBan, FaClock } from "react-icons/fa";
import { MdDateRange, MdVaccines, MdAccessTime } from "react-icons/md";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';
import { useNavigate } from "react-router-dom";

export default function AllPetVaccines() {
    const { t } = useContext(TranslatorContext);

    const [petVaccines, setPetVaccines] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Status Modal States
    const [statusModal, setStatusModal] = useState(false);
    const [selectedVaccine, setSelectedVaccine] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        customer_name: '',
        pet_name: '',
        start_date: '',
        end_date: '',
        reminder_status: ''
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);

    useEffect(() => {
        fetchPetVaccines();
    }, [currentPage, filters]);

    const fetchPetVaccines = async () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        const params = new URLSearchParams({
            page: currentPage,
            ...(filters.customer_name && { customer_name: filters.customer_name }),
            ...(filters.pet_name && { pet_name: filters.pet_name }),
            ...(filters.start_date && { start_date: filters.start_date }),
            ...(filters.end_date && { end_date: filters.end_date }),
            ...(filters.reminder_status && { reminder_status: filters.reminder_status })
        });

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/all-pet-vaccine?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setPetVaccines(response.data.petVaccines.data || []);
                setLastPage(response.data.petVaccines.last_page || 1);
                setTotal(response.data.petVaccines.total || 0);
                setPerPage(response.data.petVaccines.per_page || 10);
            }
        } catch (error) {
            console.error("Error fetching pet vaccines:", error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('failed_to_load_vaccines'),
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            customer_name: '',
            pet_name: '',
            start_date: '',
            end_date: '',
            reminder_status: ''
        });
        setCurrentPage(1);
    };

    const openStatusModal = (vaccine) => {
        setSelectedVaccine(vaccine);
        setSelectedStatus(vaccine.reminder_status || '');
        setStatusModal(true);
    };

    const handleStatusUpdate = async () => {
        if (!selectedStatus) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('please_select_status'),
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        setUpdatingStatus(true);
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-vaccine-reminder-status/${selectedVaccine.id}`,
                { reminder_status: selectedStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setStatusModal(false);
                setSelectedVaccine(null);
                setSelectedStatus('');
                fetchPetVaccines();

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('status_updated_successfully'),
                    timer: 2000,
                    showConfirmButton: true,
                    confirmButtonColor: '#3085d6'
                });
            }
        } catch (error) {
            console.error("Error updating status:", error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('failed_to_update_status'),
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setUpdatingStatus(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            completed: { class: 'mc-badge mc-badge-success', text: 'Completed', icon: <FaCheckCircle /> },
            missed: { class: 'mc-badge mc-badge-danger', text: 'Missed', icon: <FaBan /> },
            cancelled: { class: 'mc-badge mc-badge-warning', text: 'Cancelled', icon: <FaTimes /> },
            pending: { class: 'mc-badge mc-badge-info', text: 'Pending', icon: <FaClock /> }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <span className={config.class}>{config.icon} {config.text}</span>;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12; // Convert 0 to 12

            const formattedDate = `${day}-${month}-${year}`;
            const formattedTime = `${hours}:${minutes} ${ampm}`;

            return `${formattedDate} ${formattedTime}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const formatDateOnly = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            return `${day}-${month}-${year}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage) {
            setCurrentPage(newPage);
        }
    };

    if (loading && currentPage === 1) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">{t("loading")}...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('pet vaccinations')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('appointments')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('vaccinations')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Filters Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-body">
                            <Row>
                                <Col xl={3} className="mb-3">
                                    <Form.Label className="fw-bold">{t('customer name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="customer_name"
                                        value={filters.customer_name}
                                        onChange={handleFilterChange}
                                        placeholder={t('search by customer')}
                                    />
                                </Col>

                                <Col xl={3} className="mb-3">
                                    <Form.Label className="fw-bold">{t('pet name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="pet_name"
                                        value={filters.pet_name}
                                        onChange={handleFilterChange}
                                        placeholder={t('search by pet')}
                                    />
                                </Col>

                                <Col xl={2} className="mb-3">
                                    <Form.Label className="fw-bold">{t('start date')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="start_date"
                                        value={filters.start_date}
                                        onChange={handleFilterChange}
                                    />
                                </Col>

                                <Col xl={2} className="mb-3">
                                    <Form.Label className="fw-bold">{t('end date')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="end_date"
                                        value={filters.end_date}
                                        onChange={handleFilterChange}
                                    />
                                </Col>

                                <Col xl={2} className="mb-3">
                                    <Form.Label className="fw-bold">{t('status')}</Form.Label>
                                    <Form.Select
                                        name="reminder_status"
                                        value={filters.reminder_status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t('all')}</option>
                                        <option value="pending">{t('pending')}</option>
                                        <option value="completed">{t('completed')}</option>
                                        <option value="missed">{t('missed')}</option>
                                        <option value="cancelled">{t('cancelled')}</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            <Row>
                                <Col xl={12} className="d-flex justify-content-end">
                                    <ButtonComponent
                                        className="btn btn-secondary"
                                        onClick={clearFilters}
                                    >
                                        <FaTimes className="me-1" /> {t('clear filters')}
                                    </ButtonComponent>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>

                {/* Table Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header d-flex justify-content-between align-items-center">
                            <h5 className="mc-card-title mb-0">{t('vaccinations list')}</h5>
                            <span className="mc-badge mc-badge-info">
                                {t('total')}: {total}
                            </span>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>{t('appointment number')}</th>
                                                    <th>{t('pet name')}</th>
                                                    <th>{t('vaccine name')}</th>
                                                    <th>{t('vaccination date')}</th>
                                                    <th>{t('doctor')}</th>
                                                    <th>{t('Reminder date')}</th>
                                                    <th>{t('Reminder status')}</th>
                                                    <th>{t('actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {petVaccines.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center">
                                                            {t('No vaccinations found')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    petVaccines.map((vaccine, index) => (
                                                        <tr key={vaccine.id}>
                                                            <td>
                                                                <div className="mc-table-check">
                                                                    <p>{(currentPage - 1) * perPage + index + 1}</p>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="mc-badge mc-badge-primary">
                                                                    {vaccine.appointment?.appointment_number || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <strong className="d-flex align-items-center gap-2">
                                                                        {vaccine.appointment?.pet?.name || 'N/A'}
                                                                    </strong>
                                                                    <small className="text-muted">
                                                                        {vaccine.appointment?.customer?.name || 'N/A'}
                                                                    </small>
                                                                </div>
                                                            </td>

                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {vaccine.vaccine?.product?.name || vaccine.vaccine?.name || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        {formatDateTime(vaccine.vaccine_date)}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    {vaccine.appointment?.doctor?.name || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        {formatDateTime(vaccine.reminder_date)}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{getStatusBadge(vaccine.reminder_status)}</td>
                                                            <td>
                                                                <div className="mc-table-action">
                                                                    {hasPermission("Pet vaccine edit") && (

                                                                        <ButtonComponent
                                                                            type="button"
                                                                            className="material-icons edit"
                                                                            onClick={() => openStatusModal(vaccine)}
                                                                            title={t('update status')}
                                                                        >
                                                                            edit_note
                                                                        </ButtonComponent>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Pagination */}
                                        {lastPage > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top px-3 pb-3">
                                                <div className="text-muted small">
                                                    {t('showing')} {(currentPage - 1) * perPage + 1} {t('to')} {Math.min(currentPage * perPage, total)} {t('of')} {total} {t('entries')}
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        {t('previous')}
                                                    </ButtonComponent>
                                                    <span className="px-3 py-1 bg-light rounded">
                                                        {t('page')} {currentPage} {t('of')} {lastPage}
                                                    </span>
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === lastPage}
                                                    >
                                                        {t('next')}
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            {/* Status Update Modal */}
            <Modal
                show={statusModal}
                onHide={() => setStatusModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        <FaBell className="me-2" />
                        {t('update vaccination status')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={(e) => e.preventDefault()}>
                    <Modal.Body className="custom-modal-body">
                        {selectedVaccine && (
                            <>
                                <div className="alert alert-info mb-3">
                                    <div><strong>{t('appointment')}:</strong> #{selectedVaccine.appointment?.appointment_number}</div>
                                    <div><strong>{t('pet')}:</strong> {selectedVaccine.appointment?.pet?.name}</div>
                                    <div><strong>{t('vaccine')}:</strong> {selectedVaccine.vaccine?.product?.name || selectedVaccine.vaccine?.name}</div>
                                    <div><strong>{t('vaccination date')}:</strong> {formatDateTime(selectedVaccine.vaccine_date)}</div>
                                    <div><strong>{t('reminder date')}:</strong> {formatDateTime(selectedVaccine.reminder_date)}</div>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('reminder status')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                        className="custom-input"
                                    >
                                        <option value="">{t('select status')}</option>
                                        <option value="completed">{t('completed')}</option>
                                        <option value="missed">{t('missed')}</option>
                                        <option value="cancelled">{t('cancelled')}</option>
                                    </Form.Select>
                                </Form.Group>

                                <div className="alert alert-warning mt-3">
                                    <FaTimes className="me-2" />
                                    <small>{t('status_change_warning')}</small>
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setStatusModal(false)}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            type="button"
                            className="btn btn-primary px-4"
                            onClick={handleStatusUpdate}
                            disabled={updatingStatus}
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                        >
                            {updatingStatus && <span className="spinner-border spinner-border-sm me-2"></span>}
                            {t('update status')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style jsx>{`
                .custom-modal-dialog {
                    max-width: 500px;
                }
                .custom-modal-content {
                    border-radius: 12px;
                }
                .custom-modal-header {
                    background-color: #d7d8da;
                    border-bottom: none;
                }
                .custom-modal-header .btn-close {
                    filter: brightness(0) invert(1);
                }
                .custom-modal-title {
                    font-weight: 600;
                }
                .custom-modal-body {
                    padding: 20px;
                }
                .custom-modal-footer {
                    border-top: none;
                    padding: 15px 20px;
                }
                .custom-input:focus {
                    border-color: #3182ce;
                    box-shadow: 0 0 0 0.2rem rgba(8, 88, 247, 0.25);
                }
                .mc-table-action {
                    display: flex;
                    gap: 8px;
                }
                .mc-table-action .material-icons {
                    cursor: pointer;
                    font-size: 20px;
                }
                .mc-table-action .material-icons.edit {
                    color: #3182ce;
                }
                .mc-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .mc-badge-success {
                    background-color: #d4edda;
                    color: #155724;
                }
                .mc-badge-danger {
                    background-color: #f8d7da;
                    color: #721c24;
                }
                .mc-badge-warning {
                    background-color: #fff3cd;
                    color: #856404;
                }
                .mc-badge-info {
                    background-color: #d1ecf1;
                    color: #0c5460;
                }
                .mc-badge-primary {
                    background-color: #cfe2ff;
                    color: #084298;
                }
            `}</style>
        </PageLayout>
    );
}